/**
 * Filtros de población de las gráficas del Panorama (V-21; REQ-MINT-006 AC-02).
 *
 * P0: fabricante, proveedor, modelos, rango de rendimiento, habilidad/ámbito,
 * búsqueda/reset. P1: precio cliente, capacidades/contexto, lifecycle,
 * cobertura/frescura, open weights/licencia, corte de conocimiento y Elo/precio.
 *
 * Todo el cálculo es puro y client-side sobre el `/models` ya paginado. No se
 * imputan valores: un modelo sin el dato requerido queda fuera del filtro.
 */

import type { ModelListResponse, OfferingIntelligence } from "../types";
import { publisherSlug } from "./publisher";
import {
  deriveOverallPerformance,
  isPublicBenchmarkSource,
  SKILL_BENCHMARKS,
  type SkillKey,
} from "./panorama";

export type PerformanceRange = "all" | "top" | "mid" | "base";
export type PanoramaSkill = "general" | SkillKey;
export type OpenWeightsFilter = "any" | "open" | "proprietary";
export type PanoramaSort = "elo_desc" | "elo_asc" | "value_desc" | "name";

export const PANORAMA_CAPABILITIES = [
  "tools",
  "vision",
  "audio",
  "reasoning",
  "structured_output",
  "streaming",
  "caching",
  "embeddings",
] as const;

export type PanoramaCapability = (typeof PANORAMA_CAPABILITIES)[number];

export interface PanoramaFilters {
  publishers: string[];
  providers: string[];
  modelRefs: string[];
  range: PerformanceRange;
  skill: PanoramaSkill;
  priceInputMin: number | null;
  priceInputMax: number | null;
  priceOutputMin: number | null;
  priceOutputMax: number | null;
  contextMin: number | null;
  capabilities: PanoramaCapability[];
  lifecycle: string[];
  minDomains: number;
  publishedWithinDays: number | null;
  openWeights: OpenWeightsFilter;
  licenses: string[];
  knowledgeCutoffAfter: string | null;
  eloPerUsdMin: number | null;
  eloPerUsdMax: number | null;
  limit: number;
  sort: PanoramaSort;
}

export interface PanoramaFilterOption {
  value: string;
  label: string;
}

export interface PanoramaFilterOptions {
  publishers: PanoramaFilterOption[];
  providers: PanoramaFilterOption[];
  models: PanoramaFilterOption[];
  licenses: PanoramaFilterOption[];
  lifecycles: PanoramaFilterOption[];
  capabilities: PanoramaFilterOption[];
}

export interface PanoramaRangeCounts {
  all: number;
  top: number;
  mid: number;
  base: number;
}

export const DEFAULT_PANORAMA_FILTERS: PanoramaFilters = {
  publishers: [],
  providers: [],
  modelRefs: [],
  range: "all",
  skill: "general",
  priceInputMin: null,
  priceInputMax: null,
  priceOutputMin: null,
  priceOutputMax: null,
  contextMin: null,
  capabilities: [],
  lifecycle: [],
  minDomains: 0,
  publishedWithinDays: null,
  openWeights: "any",
  licenses: [],
  knowledgeCutoffAfter: null,
  eloPerUsdMin: null,
  eloPerUsdMax: null,
  limit: 20,
  sort: "elo_desc",
};

const PUBLISHER_LABELS: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  meta: "Meta",
  mistral: "Mistral",
  amazon: "Amazon",
  cohere: "Cohere",
  deepseek: "DeepSeek",
  xai: "xAI",
  microsoft: "Microsoft",
  alibaba: "Alibaba",
  qwen: "Qwen",
  moonshotai: "Moonshot AI",
  "voyage-ai": "Voyage AI",
  nvidia: "NVIDIA",
  ai21: "AI21",
  perplexity: "Perplexity",
};

const LIFECYCLE_ORDER = ["ga", "stable", "preview", "deprecated", "retired", "unknown"];

interface ModelFacet {
  ref: string;
  displayName: string;
  publishers: Set<string>;
  providers: Set<string>;
  inputPrice: number | null;
  outputPrice: number | null;
  context: number | null;
  capabilities: Set<PanoramaCapability>;
  lifecycles: Set<string>;
  openWeights: boolean | null;
  license: string | null;
  knowledgeCutoff: string | null;
  domains: Set<string>;
  latestPublishedAt: string | null;
  overall: { score: number; unit: "elo" | "percentile" } | null;
  skillScores: Record<SkillKey, number | null>;
  eloPerUsd: number | null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function priceOf(item: OfferingIntelligence, key: "input" | "output"): number | null {
  if (item.client_price?.state !== "available") return null;
  return finiteNumber(item.client_price.components?.[key]?.usd_per_million);
}

function modalitySet(item: OfferingIntelligence): Set<string> {
  const raw = item.capabilities?.["modalities"];
  return new Set(
    Array.isArray(raw)
      ? raw.filter((value): value is string => typeof value === "string")
      : [],
  );
}

function capabilitySet(item: OfferingIntelligence): Set<PanoramaCapability> {
  const result = new Set<PanoramaCapability>();
  const capabilities = item.capabilities ?? {};
  for (const key of [
    "tools",
    "reasoning",
    "structured_output",
    "streaming",
    "caching",
    "embeddings",
  ] as const) {
    if (capabilities[key] === true) result.add(key);
  }
  const modalities = modalitySet(item);
  if (modalities.has("image") || modalities.has("vision")) result.add("vision");
  if (modalities.has("audio") || modalities.has("speech")) result.add("audio");
  return result;
}

function publisherOf(item: OfferingIntelligence): string | null {
  const raw = item.publisher ?? item.capabilities?.["publisher"];
  if (typeof raw !== "string" || !raw.trim()) return null;
  return publisherSlug(raw) || null;
}

function publisherLabel(value: string): string {
  return (
    PUBLISHER_LABELS[value] ??
    value
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

function latestPublished(domain: NonNullable<OfferingIntelligence["benchmarks"]>["domains"][number]):
  | string
  | null {
  let latest: string | null = null;
  for (const component of domain.components ?? []) {
    if (!isPublicBenchmarkSource(component.source)) continue;
    if (typeof component.published_at !== "string") continue;
    if (latest === null || component.published_at > latest) {
      latest = component.published_at;
    }
  }
  return latest;
}

function skillValue(item: OfferingIntelligence, skill: SkillKey): number | null {
  const domain = item.benchmarks?.domains?.find((entry) => entry.domain === skill);
  if (!domain) return null;
  const component = (domain.components ?? []).find(
    (entry) =>
      entry.benchmark_code === SKILL_BENCHMARKS[skill] &&
      isPublicBenchmarkSource(entry.source) &&
      Number.isFinite(entry.value),
  );
  return component ? finiteNumber(component.value) : null;
}

function buildFacets(data?: ModelListResponse): ModelFacet[] {
  const overall = new Map(
    deriveOverallPerformance(data).map((row) => [row.modelRef, row]),
  );
  const facets = new Map<string, ModelFacet>();
  for (const item of data?.items ?? []) {
    const ref = item.offering.model_ref;
    if (!ref || item.price_applicability === "not_applicable") continue;
    const facet =
      facets.get(ref) ??
      {
        ref,
        displayName: item.profile?.display_name?.trim() || ref,
        publishers: new Set<string>(),
        providers: new Set<string>(),
        inputPrice: null,
        outputPrice: null,
        context: null,
        capabilities: new Set<PanoramaCapability>(),
        lifecycles: new Set<string>(),
        openWeights: null,
        license: null,
        knowledgeCutoff: null,
        domains: new Set<string>(),
        latestPublishedAt: null,
        overall: null,
        skillScores: { codigo: null, conversacion: null, razonamiento: null },
        eloPerUsd: null,
      };

    const publisher = publisherOf(item);
    if (publisher) facet.publishers.add(publisher);
    facet.providers.add(item.offering.provider);
    if (item.profile?.display_name?.trim()) {
      facet.displayName = item.profile.display_name.trim();
    }
    if (item.profile?.open_weights != null) {
      facet.openWeights = item.profile.open_weights;
    }
    if (item.profile?.license) facet.license = item.profile.license;
    if (item.profile?.knowledge_cutoff) {
      facet.knowledgeCutoff = item.profile.knowledge_cutoff;
    }
    for (const capability of capabilitySet(item)) facet.capabilities.add(capability);
    facet.lifecycles.add(item.lifecycle_status || "unknown");

    const input = priceOf(item, "input");
    const output = priceOf(item, "output");
    if (input !== null) {
      facet.inputPrice = facet.inputPrice === null ? input : Math.min(facet.inputPrice, input);
    }
    if (output !== null) {
      facet.outputPrice =
        facet.outputPrice === null ? output : Math.min(facet.outputPrice, output);
    }
    const context = finiteNumber(item.capabilities?.["context_window"]);
    if (context !== null) {
      facet.context = facet.context === null ? context : Math.max(facet.context, context);
    }

    for (const domain of item.benchmarks?.domains ?? []) {
      const published = latestPublished(domain);
      if (!published) continue;
      facet.domains.add(domain.domain);
      if (facet.latestPublishedAt === null || published > facet.latestPublishedAt) {
        facet.latestPublishedAt = published;
      }
    }
    for (const skill of ["codigo", "conversacion", "razonamiento"] as const) {
      const value = skillValue(item, skill);
      if (value === null) continue;
      const previous = facet.skillScores[skill];
      if (previous === null || value > previous) facet.skillScores[skill] = value;
    }
    facets.set(ref, facet);
  }

  for (const facet of facets.values()) {
    const row = overall.get(facet.ref);
    facet.overall = row ? { score: row.score, unit: row.unit } : null;
    facet.eloPerUsd =
      row?.unit === "elo" && facet.inputPrice !== null && facet.inputPrice > 0
        ? row.score / facet.inputPrice
        : null;
  }
  return [...facets.values()];
}

function metricValue(facet: ModelFacet, skill: PanoramaSkill): number | null {
  return skill === "general" ? (facet.overall?.score ?? null) : facet.skillScores[skill];
}

function quantile(sorted: number[], fraction: number): number | null {
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return sorted[0];
  const position = (sorted.length - 1) * fraction;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function thresholds(facets: ModelFacet[], skill: PanoramaSkill) {
  const values = facets
    .map((facet) => metricValue(facet, skill))
    .filter((value): value is number => value !== null)
    .sort((a, b) => a - b);
  return { top: quantile(values, 0.75), mid: quantile(values, 0.25) };
}

function rangeOf(score: number, top: number | null, mid: number | null): PerformanceRange {
  if (top !== null && score >= top) return "top";
  if (mid !== null && score >= mid) return "mid";
  return "base";
}

function inRange(
  score: number | null,
  range: PerformanceRange,
  top: number | null,
  mid: number | null,
): boolean {
  if (range === "all") return true;
  if (score === null) return false;
  return rangeOf(score, top, mid) === range;
}

function inNumberRange(
  value: number | null,
  min: number | null,
  max: number | null,
): boolean {
  if (min === null && max === null) return true;
  if (value === null) return false;
  if (min !== null && value < min) return false;
  if (max !== null && value > max) return false;
  return true;
}

function referenceAgeDays(data: ModelListResponse | undefined, latest: string | null): number | null {
  if (!latest) return null;
  const reference = data?.generated_at ? new Date(data.generated_at) : new Date();
  const published = new Date(latest);
  if (Number.isNaN(reference.getTime()) || Number.isNaN(published.getTime())) return null;
  return (reference.getTime() - published.getTime()) / 86_400_000;
}

function passesFacet(
  facet: ModelFacet,
  filters: PanoramaFilters,
  data: ModelListResponse | undefined,
  top: number | null,
  mid: number | null,
): boolean {
  if (filters.publishers.length > 0) {
    if (!filters.publishers.some((publisher) => facet.publishers.has(publisher))) return false;
  }
  if (filters.providers.length > 0) {
    if (!filters.providers.some((provider) => facet.providers.has(provider))) return false;
  }
  if (filters.modelRefs.length > 0 && !filters.modelRefs.includes(facet.ref)) return false;
  if (filters.skill !== "general" && facet.skillScores[filters.skill] === null) return false;
  if (!inRange(metricValue(facet, filters.skill), filters.range, top, mid)) return false;
  if (!inNumberRange(facet.inputPrice, filters.priceInputMin, filters.priceInputMax)) return false;
  if (!inNumberRange(facet.outputPrice, filters.priceOutputMin, filters.priceOutputMax)) {
    return false;
  }
  if (filters.contextMin !== null) {
    if (facet.context === null || facet.context < filters.contextMin) return false;
  }
  if (!filters.capabilities.every((capability) => facet.capabilities.has(capability))) {
    return false;
  }
  if (filters.lifecycle.length > 0) {
    if (!filters.lifecycle.some((lifecycle) => facet.lifecycles.has(lifecycle))) return false;
  }
  if (filters.minDomains > 0 && facet.domains.size < filters.minDomains) return false;
  if (filters.publishedWithinDays !== null) {
    const age = referenceAgeDays(data, facet.latestPublishedAt);
    if (age === null || age > filters.publishedWithinDays) return false;
  }
  if (filters.openWeights === "open" && facet.openWeights !== true) return false;
  if (filters.openWeights === "proprietary" && facet.openWeights !== false) return false;
  if (filters.licenses.length > 0) {
    if (!facet.license || !filters.licenses.includes(facet.license)) return false;
  }
  if (filters.knowledgeCutoffAfter !== null) {
    if (!facet.knowledgeCutoff) return false;
    if (facet.knowledgeCutoff.slice(0, 7) < filters.knowledgeCutoffAfter) return false;
  }
  if (!inNumberRange(facet.eloPerUsd, filters.eloPerUsdMin, filters.eloPerUsdMax)) {
    return false;
  }
  return true;
}

/** Aplica los filtros compartidos y devuelve solo las ofertas de los modelos que pasan. */
export function applyPanoramaFilters(
  data: ModelListResponse | undefined,
  filters: PanoramaFilters,
): { data: ModelListResponse | undefined; matchedModels: number; totalModels: number } {
  if (!data) return { data, matchedModels: 0, totalModels: 0 };
  const facets = buildFacets(data);
  const { top, mid } = thresholds(facets, filters.skill);
  const allowed = new Set(
    facets
      .filter((facet) => passesFacet(facet, filters, data, top, mid))
      .map((facet) => facet.ref),
  );
  const items = data.items.filter((item) => {
    const ref = item.offering.model_ref;
    if (!ref || !allowed.has(ref)) return false;
    if (filters.providers.length > 0 && !filters.providers.includes(item.offering.provider)) {
      return false;
    }
    return true;
  });
  return {
    data: { ...data, items, total: items.length },
    matchedModels: allowed.size,
    totalModels: facets.length,
  };
}

/** Opciones visibles para los selectores (solo valores presentes en el catálogo). */
export function derivePanoramaFilterOptions(
  data?: ModelListResponse,
): PanoramaFilterOptions {
  const facets = buildFacets(data);
  const publishers = new Map<string, string>();
  const providers = new Set<string>();
  const models = new Map<string, string>();
  const licenses = new Set<string>();
  const lifecycles = new Set<string>();
  for (const facet of facets) {
    for (const publisher of facet.publishers) {
      publishers.set(publisher, publisherLabel(publisher));
    }
    for (const provider of facet.providers) providers.add(provider);
    models.set(facet.ref, facet.displayName);
    if (facet.license) licenses.add(facet.license);
    for (const lifecycle of facet.lifecycles) lifecycles.add(lifecycle);
  }
  return {
    publishers: [...publishers.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    providers: [...providers]
      .sort()
      .map((value) => ({ value, label: value.replace(/_/g, " ") })),
    models: [...models.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    licenses: [...licenses].sort().map((value) => ({ value, label: value })),
    lifecycles: [...lifecycles]
      .sort(
        (a, b) =>
          (LIFECYCLE_ORDER.indexOf(a) === -1 ? 99 : LIFECYCLE_ORDER.indexOf(a)) -
          (LIFECYCLE_ORDER.indexOf(b) === -1 ? 99 : LIFECYCLE_ORDER.indexOf(b)),
      )
      .map((value) => ({ value, label: value })),
    capabilities: PANORAMA_CAPABILITIES.map((value) => ({ value, label: value })),
  };
}

/** Conteos por rango sobre el universo con dato (no sobre el subconjunto filtrado). */
export function derivePanoramaRangeCounts(
  data: ModelListResponse | undefined,
  skill: PanoramaSkill,
): PanoramaRangeCounts {
  const facets = buildFacets(data);
  const { top, mid } = thresholds(facets, skill);
  const counts: PanoramaRangeCounts = { all: 0, top: 0, mid: 0, base: 0 };
  for (const facet of facets) {
    const value = metricValue(facet, skill);
    if (value === null) continue;
    counts.all += 1;
    counts[rangeOf(value, top, mid)] += 1;
  }
  return counts;
}

/** Elo/precio (Elo LMArena por USD/1M de entrada) por modelo; sin dato ⇒ ausente. */
export function deriveEloPerUsd(data?: ModelListResponse): Map<string, number> {
  return new Map(
    buildFacets(data)
      .filter((facet): facet is ModelFacet & { eloPerUsd: number } => facet.eloPerUsd !== null)
      .map((facet) => [facet.ref, facet.eloPerUsd]),
  );
}

/** Número de categorías de filtro activas (para el badge de la barra). */
export function countActiveFilters(filters: PanoramaFilters): number {
  let count = 0;
  if (filters.publishers.length > 0) count += 1;
  if (filters.providers.length > 0) count += 1;
  if (filters.modelRefs.length > 0) count += 1;
  if (filters.range !== "all") count += 1;
  if (filters.skill !== "general") count += 1;
  if (
    filters.priceInputMin !== null ||
    filters.priceInputMax !== null ||
    filters.priceOutputMin !== null ||
    filters.priceOutputMax !== null
  ) {
    count += 1;
  }
  if (filters.contextMin !== null) count += 1;
  if (filters.capabilities.length > 0) count += 1;
  if (filters.lifecycle.length > 0) count += 1;
  if (filters.minDomains > 0) count += 1;
  if (filters.publishedWithinDays !== null) count += 1;
  if (filters.openWeights !== "any") count += 1;
  if (filters.licenses.length > 0) count += 1;
  if (filters.knowledgeCutoffAfter !== null) count += 1;
  if (filters.eloPerUsdMin !== null || filters.eloPerUsdMax !== null) count += 1;
  return count;
}

function parseNumber(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseList(value: string | null): string[] {
  if (!value) return [];
  return [...new Set(value.split(",").map((entry) => entry.trim()).filter(Boolean))];
}

export function parsePanoramaFilters(params: URLSearchParams): PanoramaFilters {
  const range = params.get("range");
  const skill = params.get("skill");
  const openWeights = params.get("open_weights");
  const sort = params.get("sort");
  const limit = parseNumber(params.get("limit"));
  return {
    publishers: parseList(params.get("publishers")),
    providers: parseList(params.get("providers")),
    modelRefs: parseList(params.get("models")),
    range:
      range === "top" || range === "mid" || range === "base" ? range : "all",
    skill:
      skill === "codigo" || skill === "conversacion" || skill === "razonamiento"
        ? skill
        : "general",
    priceInputMin: parseNumber(params.get("price_in_min")),
    priceInputMax: parseNumber(params.get("price_in_max")),
    priceOutputMin: parseNumber(params.get("price_out_min")),
    priceOutputMax: parseNumber(params.get("price_out_max")),
    contextMin: parseNumber(params.get("context_min")),
    capabilities: parseList(params.get("caps")).filter((value): value is PanoramaCapability =>
      (PANORAMA_CAPABILITIES as readonly string[]).includes(value),
    ),
    lifecycle: parseList(params.get("lifecycle")),
    minDomains: parseNumber(params.get("min_domains")) ?? 0,
    publishedWithinDays: parseNumber(params.get("fresh")),
    openWeights:
      openWeights === "open" || openWeights === "proprietary" ? openWeights : "any",
    licenses: parseList(params.get("licenses")),
    knowledgeCutoffAfter: params.get("cutoff") || null,
    eloPerUsdMin: parseNumber(params.get("elo_usd_min")),
    eloPerUsdMax: parseNumber(params.get("elo_usd_max")),
    limit: limit === 0 || limit === 10 || limit === 20 || limit === 50 ? limit : 20,
    sort:
      sort === "elo_asc" || sort === "value_desc" || sort === "name"
        ? sort
        : "elo_desc",
  };
}

export function serializePanoramaFilters(filters: PanoramaFilters): URLSearchParams {
  const params = new URLSearchParams();
  const setList = (key: string, values: string[]) => {
    if (values.length > 0) params.set(key, values.join(","));
  };
  setList("publishers", filters.publishers);
  setList("providers", filters.providers);
  setList("models", filters.modelRefs);
  if (filters.range !== "all") params.set("range", filters.range);
  if (filters.skill !== "general") params.set("skill", filters.skill);
  for (const [key, value] of [
    ["price_in_min", filters.priceInputMin],
    ["price_in_max", filters.priceInputMax],
    ["price_out_min", filters.priceOutputMin],
    ["price_out_max", filters.priceOutputMax],
    ["context_min", filters.contextMin],
    ["elo_usd_min", filters.eloPerUsdMin],
    ["elo_usd_max", filters.eloPerUsdMax],
  ] as const) {
    if (value !== null) params.set(key, String(value));
  }
  setList("caps", filters.capabilities);
  setList("lifecycle", filters.lifecycle);
  if (filters.minDomains > 0) params.set("min_domains", String(filters.minDomains));
  if (filters.publishedWithinDays !== null) {
    params.set("fresh", String(filters.publishedWithinDays));
  }
  if (filters.openWeights !== "any") params.set("open_weights", filters.openWeights);
  setList("licenses", filters.licenses);
  if (filters.knowledgeCutoffAfter) params.set("cutoff", filters.knowledgeCutoffAfter);
  if (filters.limit !== 20) params.set("limit", String(filters.limit));
  if (filters.sort !== "elo_desc") params.set("sort", filters.sort);
  return params;
}
